import {
  createFrontendPlugin,
  createRouteRef,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import GroupWorkIcon from '@material-ui/icons/GroupWork';

/**
 * Knowledge coverage in the portal: how many people actively maintain each
 * component, derived from git history rather than a survey.
 *
 * Nothing here is per person. The card and the page both show component-level
 * counts and bands; individual scores stay in the backend for continuity
 * planning. Publishing a per-person view would turn this into a performance
 * instrument, which is out of scope and the fastest way to get it gamed.
 *
 * See docs/knowledge-coverage.md for the model and its limits.
 */

/**
 * A page only reaches the sidebar if it carries a route ref - AppNav drops
 * route nodes without `core.routing.ref`, however complete their title is.
 * Same reason processesRouteRef exists in modules/bcm.
 */
const knowledgeRiskRouteRef = createRouteRef();

/** /knowledge-risk - components below the bar their criticality sets. */
export const knowledgeRiskPage = PageBlueprint.make({
  params: {
    path: '/knowledge-risk',
    routeRef: knowledgeRiskRouteRef,
    icon: <GroupWorkIcon fontSize="inherit" />,
    title: 'Knowledge risk',
    loader: async () =>
      import('./KnowledgeRiskPage').then(m => <m.KnowledgeRiskPage />),
  },
});

/**
 * Coverage on a component overview. Also attaches to System and
 * BusinessProcess, where it renders the rolled-up band and names the weakest
 * critical child rather than an average.
 */
const coverageCard = EntityCardBlueprint.make({
  name: 'knowledge-coverage',
  params: {
    // Info column, beside About - this is a compact set of label/value rows,
    // the same shape as bcm's ProcessAboutCard. Untyped cards go to the wide
    // content column, where it would stretch and push the relations down.
    type: 'info',
    filter: entity =>
      ['component', 'system', 'businessprocess'].includes(
        entity.kind.toLocaleLowerCase('en-US'),
      ),
    loader: async () => import('./CoverageCard').then(m => <m.CoverageCard />),
  },
});

export const knowledgeCoveragePlugin = createFrontendPlugin({
  pluginId: 'knowledge-coverage',
  routes: { knowledgeRisk: knowledgeRiskRouteRef },
  extensions: [knowledgeRiskPage, coverageCard],
});
